import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, X, Clock } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Checkbox } from "../../components/ui/checkbox";

export const DOC_LABELS = {
  registration: "Current vehicle registration",
  insurance: "Current insurance card",
  inspection: "Current state vehicle inspection (if applicable)",
  vin_photo: "VIN plate or VIN label photograph",
  rental_agreement: "Rental agreement",
  photo_front: "Front of vehicle",
  photo_rear: "Rear of vehicle",
  photo_driver: "Driver's side",
  photo_passenger: "Passenger's side",
  photo_cargo: "Cargo area (empty and visible)",
  pickup_bed_photo: "Covered pickup bed",
  pickup_lock_photo: "Locking mechanism",
};

export default function DocUpload({ docKey, label, doc = {}, onChange, accept = ".pdf,.jpg,.jpeg,.png,.heic,.heif,image/*,application/pdf", allowDefer = true, allowNA = false, required = false, imageOnly = false }) {
  const [busy, setBusy] = useState(false);
  const title = label || DOC_LABELS[docKey];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File must be 10MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/avp-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...doc, file_id: data.file_id, filename: data.filename, defer: false, not_applicable: false });
      toast.success(`${title} uploaded`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const state = doc.file_id ? "uploaded" : doc.not_applicable ? "na" : doc.defer ? "defer" : "empty";

  return (
    <div data-testid={`avp-doc-${docKey}`} className={`rounded-xl border p-4 ${state === "uploaded" ? "border-emerald-500/40 bg-emerald-500/5" : state === "defer" ? "border-slate-700 bg-slate-900/40" : "border-slate-700 bg-slate-950"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">{title}{required && <span className="text-amber-400"> *</span>}</div>
          {state === "uploaded" && <div className="mt-1 text-xs text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {doc.filename || "Uploaded"}</div>}
          {state === "defer" && <div className="mt-1 text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Will provide during onboarding</div>}
          {state === "na" && <div className="mt-1 text-xs text-slate-400">Not applicable</div>}
        </div>
        {state === "uploaded" && (
          <button type="button" data-testid={`avp-doc-${docKey}-remove`} onClick={() => onChange({ ...doc, file_id: null, filename: null })} className="text-slate-500 hover:text-rose-400"><X className="w-4 h-4" /></button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-slate-600 bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-200 hover:border-amber-500/60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {state === "uploaded" ? "Replace file" : imageOnly ? "Upload photo" : "Upload file"}
          <input data-testid={`avp-doc-${docKey}-input`} type="file" accept={imageOnly ? "image/*,.heic,.heif" : accept} className="hidden" onChange={onFile} disabled={busy} />
        </label>
        {allowDefer && state !== "uploaded" && (
          <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <Checkbox data-testid={`avp-doc-${docKey}-defer`} checked={!!doc.defer} onCheckedChange={(v) => onChange({ ...doc, defer: !!v, not_applicable: false })} /> I will provide this during onboarding
          </label>
        )}
        {allowNA && state !== "uploaded" && (
          <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <Checkbox data-testid={`avp-doc-${docKey}-na`} checked={!!doc.not_applicable} onCheckedChange={(v) => onChange({ ...doc, not_applicable: !!v, defer: false })} /> Not applicable to my vehicle
          </label>
        )}
      </div>
    </div>
  );
}
